/** Now Dashboard — Anomalies tab: pending anomalies from the last 7 days with
 *  ack / dismiss / escalate actions. HR/managers only.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, CheckCircle2, XCircle, ArrowUpCircle, Loader2, MapPin, Wifi } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { EmployeeName } from "@/lib/employeeProfile";

const ANOM_LABEL = { new_device: "New device", new_location: "New location", new_ip: "New IP" };

export default function NowAnomaliesTab() {
  const { activeWorkspaceId: wsId } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [includeResolved, setIncludeResolved] = useState(false);
  const [dialog, setDialog] = useState(null); // {row, action}

  const load = () => {
    if (!wsId) return;
    setBusy(true);
    api.get(`/workspaces/${wsId}/now/anomalies?days=7&include_resolved=${includeResolved}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId, includeResolved]);

  return (
    <div className="space-y-3" data-testid="now-anomalies-tab">
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
          <input type="checkbox" checked={includeResolved} onChange={(e) => setIncludeResolved(e.target.checked)} data-testid="anom-include-resolved" />
          Include resolved
        </label>
        <span className="text-[11px] text-muted-foreground ml-auto">{busy ? "Loading…" : `${data?.count || 0} anomal${(data?.count || 0) === 1 ? "y" : "ies"}`}</span>
      </div>
      <div className="border" data-testid="now-anomalies-list">
        {!data && !busy && <div className="p-6 text-sm text-muted-foreground text-center">Loading…</div>}
        {data && data.rows.length === 0 && (
          <div className="p-8 text-center text-sm text-muted-foreground">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-500" />
            <p>All clear — no {includeResolved ? "" : "pending "}anomalies in the last 7 days.</p>
          </div>
        )}
        {data?.rows.map((r) => (
          <AnomalyRow key={`${r.user_id}-${r.date}`} r={r} onAction={(a) => setDialog({ row: r, action: a })} />
        ))}
      </div>
      {dialog && (
        <ReviewDialog wsId={wsId} row={dialog.row} action={dialog.action}
                      onClose={() => setDialog(null)} onDone={() => { setDialog(null); load(); }} />
      )}
    </div>
  );
}

function AnomalyRow({ r, onAction }) {
  const hasActive = (r.active_anomalies || []).length > 0;
  const rev = r.review;
  return (
    <div className="border-t px-3 py-2.5 flex flex-wrap items-center gap-2 text-xs" data-testid={`anom-row-${r.user_id}-${r.date}`}>
      <AlertTriangle className={`w-3.5 h-3.5 ${hasActive ? "text-orange-500" : "text-muted-foreground"}`} />
      <div className="min-w-40">
        <EmployeeName userId={r.user_id} name={r.name} className="font-semibold" />
        <div className="text-[10px] text-muted-foreground truncate">{[r.designation, r.department].filter(Boolean).join(" · ")}</div>
      </div>
      <span className="font-mono">{r.date}</span>
      <div className="flex flex-wrap gap-1">
        {(r.active_anomalies || []).map((a) => (
          <Badge key={a} variant="outline" className="rounded-none border-orange-500/40 text-orange-600 text-[9px]">{ANOM_LABEL[a] || a}</Badge>
        ))}
        {(r.resolved_anomalies || []).map((a) => (
          <Badge key={a} variant="outline" className="rounded-none border-emerald-500/40 text-emerald-600 text-[9px] line-through">{ANOM_LABEL[a] || a}</Badge>
        ))}
      </div>
      <div className="flex items-center gap-2 text-[10px] text-muted-foreground min-w-24">
        {r.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /><span className="truncate max-w-32">{r.location}</span></span>}
        {r.ip && <span className="inline-flex items-center gap-1"><Wifi className="w-3 h-3" />{r.ip}</span>}
      </div>
      {rev && (
        <Badge variant="outline" className={`rounded-none text-[9px] ${rev.action === "ack" ? "text-emerald-600" : rev.action === "dismiss" ? "text-muted-foreground" : "text-red-500"}`}>
          {rev.action} · {rev.reviewed_by_name}
        </Badge>
      )}
      <div className="ml-auto flex gap-1">
        {hasActive && (
          <>
            <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={() => onAction("ack")} data-testid={`anom-ack-${r.user_id}-${r.date}`}>
              <CheckCircle2 className="w-3 h-3 mr-1 text-emerald-500" />Ack
            </Button>
            <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={() => onAction("dismiss")} data-testid={`anom-dismiss-${r.user_id}-${r.date}`}>
              <XCircle className="w-3 h-3 mr-1 text-muted-foreground" />Dismiss
            </Button>
            <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={() => onAction("escalate")} data-testid={`anom-escalate-${r.user_id}-${r.date}`}>
              <ArrowUpCircle className="w-3 h-3 mr-1 text-red-500" />Escalate
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

const ACTION_LABEL = { ack: "Acknowledge", dismiss: "Dismiss", escalate: "Escalate" };

function ReviewDialog({ wsId, row, action, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/now/anomalies/review`, {
        user_id: row.user_id, date: row.date,
        anomalies: row.active_anomalies || [], action, note: note || null,
      });
      toast.success(`Anomaly ${action === "ack" ? "acknowledged" : action + "ed"}`);
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Review failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-none" data-testid="anom-review-dialog">
        <DialogHeader>
          <DialogTitle>{ACTION_LABEL[action]} anomaly · {row.name}</DialogTitle>
        </DialogHeader>
        <div className="text-xs space-y-2">
          <p><b>{row.date}</b> — {(row.active_anomalies || []).map((a) => ANOM_LABEL[a] || a).join(", ")}</p>
          {action === "escalate" && <p className="text-red-500">This will notify the employee.</p>}
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note (optional)…" className="rounded-none" data-testid="anom-review-note" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} className="rounded-none" data-testid="anom-review-cancel">Cancel</Button>
          <Button onClick={submit} className="rounded-none" disabled={busy} data-testid="anom-review-submit">
            {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}{ACTION_LABEL[action]}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
